import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { PopoverCombobox, PopoverComboboxOption } from '@/components/ui/custom/PopoverCombobox';
import { FormControl, FormDescription, FormItem, FormLabel } from '@/components/ui/form';
import { DeepgramIcon } from '@/modules/developerTools/icons/Deepgram';
import { ElevenLabsIcon } from '@/modules/developerTools/icons/ElevenLabs';
import { SonioxIcon } from '@/modules/developerTools/icons/Soniox';
import { STTModelInfo, STTProviderInternal } from '@/shared/types/voiceProviders';

const PROVIDER_NAMES: Record<STTProviderInternal, string> = {
  deepgram: 'Deepgram',
  elevenlabs: 'ElevenLabs',
  soniox: 'Soniox',
};

function getDisplayName(model: STTModelInfo): string {
  return `${PROVIDER_NAMES[model.provider]} · ${model.name}`;
}

interface STTModelSelectorProps {
  value: string;
  onChange: (model: string) => void;
  models: STTModelInfo[];
  language?: string;
  disabled?: boolean;
}

export function STTModelSelector({
  value,
  onChange,
  models,
  language,
  disabled,
}: STTModelSelectorProps) {
  const { t } = useTranslation();

  // Filter by language if provided and convert to combobox options
  // Always include the currently selected model so its label can be displayed
  const options: PopoverComboboxOption[] = useMemo(() => {
    const filtered = language
      ? models.filter((m) => m.supportedLanguages.includes(language))
      : models;

    // Ensure selected model is always included for display purposes
    const selectedModel = models.find((m) => m.id === value);
    const modelsToShow =
      selectedModel && !filtered.some((m) => m.id === value)
        ? [selectedModel, ...filtered]
        : filtered;

    return modelsToShow.map((model) => ({
      value: model.id,
      label: getDisplayName(model),
      icon: (() => {
        switch (model.provider) {
          case 'deepgram':
            return <DeepgramIcon className="size-4" />;
          case 'elevenlabs':
            return <ElevenLabsIcon className="size-4" />;
          default:
            return <SonioxIcon className="size-4" />;
        }
      })(),
    }));
  }, [models, language, value]);

  return (
    <FormItem className="flex flex-col">
      <FormLabel>{t('integrations.voice_common.transcription.model')}</FormLabel>
      <FormControl>
        <PopoverCombobox
          options={options}
          value={value}
          onChange={onChange}
          triggerPlaceholder={t('integrations.voice_common.transcription.select_model')}
          disabled={disabled}
          contentClassName="w-80"
        />
      </FormControl>
      <FormDescription>
        {t('integrations.voice_common.transcription.model_description')}
      </FormDescription>
    </FormItem>
  );
}
